import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";

const PWAContext = createContext(null);

// Detect iOS Safari (and iPadOS) which doesn't fire beforeinstallprompt
function detectIOS() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  const isIPad = /iPad|Macintosh/.test(ua) && typeof navigator.maxTouchPoints === "number" && navigator.maxTouchPoints > 1;
  const isIPhone = /iPhone|iPod/.test(ua);
  const isSafari = /Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS|OPiOS/.test(ua);
  return (isIPad || isIPhone) && isSafari;
}

function detectStandalone() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}

export function PWAInstallProvider({ children }) {
  const deferredPromptRef = useRef(null);
  const [canInstall, setCanInstall] = useState(false);
  const [isStandalone, setIsStandalone] = useState(detectStandalone());
  const [isIOS] = useState(detectIOS());
  const [showIOSModal, setShowIOSModal] = useState(false);

  useEffect(() => {
    const onBeforeInstall = (e) => {
      e.preventDefault();
      deferredPromptRef.current = e;
      setCanInstall(true);
    };
    const onInstalled = () => {
      deferredPromptRef.current = null;
      setCanInstall(false);
      setIsStandalone(true);
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);
    window.addEventListener("appinstalled", onInstalled);

    // Watch for display-mode changes
    const mq = window.matchMedia?.("(display-mode: standalone)");
    const onMQ = () => setIsStandalone(detectStandalone());
    mq?.addEventListener?.("change", onMQ);

    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
      mq?.removeEventListener?.("change", onMQ);
    };
  }, []);

  const promptInstall = useCallback(async () => {
    if (isStandalone) return "already-installed";
    if (isIOS) {
      setShowIOSModal(true);
      return "ios-instructions";
    }
    const dp = deferredPromptRef.current;
    if (!dp) return "unavailable";
    dp.prompt();
    const choice = await dp.userChoice;
    deferredPromptRef.current = null;
    setCanInstall(false);
    return choice?.outcome || "dismissed";
  }, [isIOS, isStandalone]);

  // Show button when: install prompt is captured, OR iOS Safari (manual flow), AND not already standalone
  const isAvailable = !isStandalone && (canInstall || isIOS);

  const value = useMemo(
    () => ({
      canInstall,
      isStandalone,
      isIOS,
      isAvailable,
      promptInstall,
      showIOSModal,
      setShowIOSModal,
    }),
    [canInstall, isStandalone, isIOS, isAvailable, promptInstall, showIOSModal]
  );

  return <PWAContext.Provider value={value}>{children}</PWAContext.Provider>;
}

export function usePWAInstall() {
  const ctx = useContext(PWAContext);
  if (!ctx) {
    // Safe fallback so consumers don't crash if provider is missing
    return {
      canInstall: false,
      isStandalone: false,
      isIOS: false,
      isAvailable: false,
      promptInstall: async () => "no-provider",
      showIOSModal: false,
      setShowIOSModal: () => {},
    };
  }
  return ctx;
}
